/**
 * Phase 1 Item 5 — Link-first remote consent dispatch on Subject Detail.
 *
 * Coordinators no longer see or type the PIN or the subject's email/phone —
 * the link goes out to whichever contact channels are on file for the subject
 * (captured in wizard step 3, `ctms_subject_phi`). The OTP is issued only
 * when the subject actually clicks the link, from the remote consent page.
 *
 * Panel hides when the subject has no draft / unsigned consent record.
 */
import { useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MailPlus } from 'lucide-react';
import { consent, type ConsentRecordRow } from '@/api/client';

export function RemoteConsentTrigger({ subjectId }: { subjectId: string }) {
  const { data, refetch } = useQuery({
    queryKey: ['consent', 'records', subjectId],
    queryFn: () => consent.listRecordsForSubject(subjectId),
    enabled: !!subjectId,
  });

  const unsigned = useMemo<ConsentRecordRow | null>(() => {
    const rows = data?.records ?? [];
    return rows.find((r) => !r.signed_at && !r.declined_at) ?? null;
  }, [data]);

  const send = useMutation({
    mutationFn: () => consent.sendLink(unsigned!.consent_record_id),
    onSuccess: (r) => {
      const channels = r.delivered.join(' + ');
      toast.success(`Signing link sent via ${channels}. The subject will request their PIN from the link.`);
      refetch();
    },
    onError: (e: Error) => {
      // Item 4 gate — backend returns a specific message when neither
      // email nor phone is on file. Surface that verbatim so the
      // coordinator knows to go back to Subject Detail and capture it.
      toast.error(e.message || 'Send failed.');
    },
  });

  if (!unsigned) return null;

  return (
    <section className="rounded border border-surface-border bg-surface-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
            <MailPlus className="h-4 w-4 text-accent-700" />
            Remote consent — pending signature
          </h2>
          <p className="mt-1 text-xs text-ink-muted">
            Consent record <span className="font-mono">{unsigned.consent_record_id.slice(0, 8)}…</span> is in progress.
            Send the signing link to the subject's on-file email and mobile.
          </p>
          <p className="mt-1 text-[11px] text-ink-muted">
            The subject will request their own single-use PIN from the link page — the PIN never
            passes through this screen.
          </p>
        </div>
        <button
          type="button"
          disabled={send.isPending}
          onClick={() => send.mutate()}
          className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
        >
          {send.isPending ? 'Sending…' : 'Send signing link'}
        </button>
      </div>
    </section>
  );
}
